import { useEffect, useRef } from "react";

import { useNavigate } from "react-router-dom";
import Cookies from 'js-cookie';
import { useToast } from "@chakra-ui/react";
import { UserManager } from "oidc-client-ts";
import { HttpStatusCode } from "axios";

import { DURATION_TOAST } from "@/common/constants";
import { appConfig } from "@/common/config";
import Loader from "@/components/Layouts/Loader";
import { useAppDispatch } from "@/redux/hooks";
import { setAppId, setCompanyName, setIsReviewer, setName, setRole, setUserId } from "@/redux/slices/accountSlice";
import { setDataTotalNewInspectingCompany, setDataTotalNewInspectionFee, setDataTotalNewInspectionProcess, setDataTotalNewInspector, setDataTotalNewInvoiceInspectingCompany, setDataTotalNewInvoiceShipOperator, setDataTotalNewInvoiceShipOperatorAdmin, setDataTotalNewNominationInspector, setDataTotalNewNominationShipOperator, setDataTotalNewShipOperator, setDataTotalNewSireReview, setDataTotalNewVessel } from "@/redux/slices/notificationSlice";
import { setDataMenus } from "@/redux/slices/menuSlice";
import { handleOAuthCallback } from "@/services/authService";
import { getCurrentUser } from "@/services/userService";
import { getAppIdByUserId, getUserProfile } from "@/services/inspectionUserService";
import { getInspectorById } from "@/services/inspectorService";
import { getShipOwnerById } from "@/services/shipOwnerService";
import { getNewRecordCounterAdminFinance, getNewRecordCounterInspectingCompany, getNewRecordCounterShipOperator, getNewRecordCounterSuperAdmin } from "@/services/newRecordCounterService";
import { getMenus } from "@/services/menuService";

const AuthCallback = ({ handleSetIsAuthenticated }: any) => {
    const isProcessed = useRef(false);
    const navigate = useNavigate();
    const toast = useToast();
    const dispatch = useAppDispatch();

    const userManager = new UserManager(appConfig.settings);

    const showErrorToast = (message: string) => {
        toast({
            title: 'Error Auth',
            description: message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });

        setTimeout(async () => {
            await userManager.clearStaleState();
            await userManager.signoutRedirect();
        }, 2000);
    };

    const handleUserRole = async (role: string, userId: string, menus: any) => {
        const resp = await getAppIdByUserId(userId) as any;
        if (resp.status === HttpStatusCode.Ok) {
            let companyName = 'Unknown Company';
            let appId = resp.data.appId;
            let userId = resp.data.userId;

            if (role === 'inspectingcompany') {
                const inspectorResp = await getInspectorById(appId) as any;
                if (inspectorResp.status === HttpStatusCode.Ok) {
                    companyName = inspectorResp.data.companyName;
                }
                dispatch(setIsReviewer(inspectorResp?.data?.isReviewer || false));
                dispatch(setRole('inspecting company'));

                const responseCounter = await getNewRecordCounterInspectingCompany(appId) as any;
                if (responseCounter?.status === HttpStatusCode.Ok) {
                    dispatch(setDataTotalNewNominationInspector(responseCounter?.data?.nominationInspector || 0));
                    dispatch(setDataTotalNewSireReview(responseCounter?.data?.sireReview || 0));
                    dispatch(setDataTotalNewInspectionProcess(responseCounter?.data?.inspectionProcess || 0));
                    dispatch(setDataTotalNewInvoiceInspectingCompany(responseCounter?.data?.invoices || 0));
                }

                const firstRoute = menus[0]?.isParent ? menus[0]?.children[0]?.url : menus[0]?.url;
                navigate(firstRoute);
            } else if (role === 'shipoperator') {
                const shipOwnerResp = await getShipOwnerById(appId) as any;
                if (shipOwnerResp.status === HttpStatusCode.Ok) {
                    companyName = shipOwnerResp.data.onBehalfCompany;
                }
                dispatch(setRole('ship operator'));

                const responseCounter = await getNewRecordCounterShipOperator(appId) as any;
                if (responseCounter?.status === HttpStatusCode.Ok) {
                    dispatch(setDataTotalNewNominationShipOperator(responseCounter?.data?.nominationShipOperator || 0));
                    dispatch(setDataTotalNewInvoiceShipOperator(responseCounter?.data?.invoices || 0));
                    dispatch(setDataTotalNewInspectionProcess(responseCounter?.data?.inspectionProcess || 0));
                }

                const firstRoute = menus[0]?.isParent ? menus[0]?.children[0]?.url : menus[0]?.url;
                navigate(firstRoute);
            } else if (role === 'adminfinance') {
                companyName = 'Pertamina';
                dispatch(setRole('admin finance'));

                const responseCounter = await getNewRecordCounterAdminFinance() as any;
                if (responseCounter?.status === HttpStatusCode.Ok) {
                    dispatch(setDataTotalNewInvoiceInspectingCompany(responseCounter?.data?.inspectingCompany || 0));
                    dispatch(setDataTotalNewInvoiceShipOperator(responseCounter?.data?.shipOperator || 0));
                }

                const firstRoute = menus[0]?.isParent ? menus[0]?.children[0]?.url : menus[0]?.url;
                navigate(firstRoute);
            } else if (role === 'requestor') {
                const requestorResp = await getUserProfile();
                if (requestorResp.status === HttpStatusCode.Ok) {
                    companyName = requestorResp.data.companyName;
                }
                dispatch(setRole('requestor'));

                // const responseCounter = await getNewRecordCounterRequestor() as any;
                // if (responseCounter?.status === HttpStatusCode.Ok) {
                //     dispatch(setDataTotalNewInspectingCompany(responseCounter?.data?.inspectingCompany || 0));
                //     dispatch(setDataTotalNewInspectionFee(responseCounter?.data?.inspectionFee || 0));
                //     dispatch(setDataTotalNewInspectionProcess(responseCounter?.data?.inspectionProcess || 0));
                //     dispatch(setDataTotalNewInspector(responseCounter?.data?.inspector || 0));
                //     dispatch(setDataTotalNewInvoiceInspectingCompany(responseCounter?.data?.invoiceInspectingCompanyAdmin || 0));
                //     dispatch(setDataTotalNewInvoiceShipOperator(responseCounter?.data?.invoiceShipOperator || 0));
                //     dispatch(setDataTotalNewInvoiceShipOperatorAdmin(responseCounter?.data?.invoiceShipOperatorAdminNewDraft || 0));
                //     dispatch(setDataTotalNewNominationInspector(responseCounter?.data?.nominationInspector || 0));
                //     dispatch(setDataTotalNewNominationShipOperator(responseCounter?.data?.nominationShipOperator || 0));
                //     dispatch(setDataTotalNewShipOperator(responseCounter?.data?.shipOperator || 0));
                //     dispatch(setDataTotalNewVessel(responseCounter?.data?.vessel || 0));
                //     dispatch(setDataTotalNewSireReview(responseCounter?.data?.sireReview || 0));
                // }

                const firstRoute = menus[0]?.isParent ? menus[0]?.children[0]?.url : menus[0]?.url;
                navigate(firstRoute);
            }

            dispatch(setCompanyName(companyName));
            dispatch(setAppId(appId));
            dispatch(setUserId(userId || ''));
        } else {
            showErrorToast('Failed to get app user id');
            handleSetIsAuthenticated(false);
        }
    };

    const processOAuthResponse = async () => {
        if (isProcessed.current) return;
        isProcessed.current = true;

        try {
            const currentUrl = window.location.href;
            const responseCallback = await handleOAuthCallback(currentUrl) as any;

            if (responseCallback?.error) {
                showErrorToast(responseCallback.message || "Error processing OAuth callback");
                return;
            }

            Cookies.set('access_token', responseCallback?.access_token);
            Cookies.set('refresh_token', responseCallback?.refresh_token || '');
            // const dateInWIB = '2024-11-03T23:35:00+07:00';
            // const date = new Date(dateInWIB);
            // const unixTimestamp = Math.floor(date.getTime() / 1000);
            Cookies.set('expired_time', responseCallback.expires_at || '');

            const response = await getCurrentUser(responseCallback?.access_token) as any;
            if (response?.status === HttpStatusCode.Ok) {
                const respStatus = await getAppIdByUserId(response.data.userId) as any;
                if (respStatus.status !== HttpStatusCode.Ok){
                    //showErrorToast(respStatus.message, "Couldn't log you in");
                    throw new Error(respStatus.message);
                }
                if (respStatus.data.registerStatus === 3) {
                    Cookies.set('profile', JSON.stringify(response.data));
                    dispatch(setName(response.data.name));
                    handleSetIsAuthenticated(true);

                    const respMenus = await getMenus() as any;
                    if (respMenus?.status === HttpStatusCode.Ok) {
                        dispatch(setDataMenus(respMenus.data));
                    }

                    if (response.data.role === 'superadmin') {
                        dispatch(setRole('super admin'));

                        const responseCounter = await getNewRecordCounterSuperAdmin() as any;
                        if (responseCounter?.status === HttpStatusCode.Ok) {
                            dispatch(setDataTotalNewInspectingCompany(responseCounter?.data?.inspectingCompany || 0));
                            dispatch(setDataTotalNewInspectionFee(responseCounter?.data?.inspectionFee || 0));
                            dispatch(setDataTotalNewInspectionProcess(responseCounter?.data?.inspectionProcess || 0));
                            dispatch(setDataTotalNewInspector(responseCounter?.data?.inspector || 0));
                            dispatch(setDataTotalNewInvoiceInspectingCompany(responseCounter?.data?.invoiceInspectingCompanyAdmin || 0));
                            dispatch(setDataTotalNewInvoiceShipOperator(responseCounter?.data?.invoiceShipOperator || 0));
                            dispatch(setDataTotalNewInvoiceShipOperatorAdmin(responseCounter?.data?.invoiceShipOperatorAdminNewDraft || 0));
                            dispatch(setDataTotalNewNominationInspector(responseCounter?.data?.nominationInspector || 0));
                            dispatch(setDataTotalNewNominationShipOperator(responseCounter?.data?.nominationShipOperator || 0));
                            dispatch(setDataTotalNewShipOperator(responseCounter?.data?.shipOperator || 0));
                            dispatch(setDataTotalNewVessel(responseCounter?.data?.vessel || 0));
                            dispatch(setDataTotalNewSireReview(responseCounter?.data?.sireReview || 0));
                        }

                        const firstRoute = respMenus?.data[0]?.isParent ? respMenus?.data[0]?.children[0]?.url : respMenus?.data[0]?.url;
                        navigate(firstRoute);
                    } else {
                        await handleUserRole(response.data.role, response.data.userId, respMenus.data);
                    }
                } else {
                    if (response.data.role === 'inspectingcompany') {
                        dispatch(setRole('inspecting company'));
                    } else if (response.data.role === 'shipoperator') {
                        dispatch(setRole('ship operator'));
                    }

                    Cookies.set('profile', JSON.stringify(response.data));
                    dispatch(setName(response.data.name));
                    handleSetIsAuthenticated(true);

                    setTimeout(() => {
                        navigate('/register-user', { state: { role: respStatus.data.role, status: respStatus.data.registerStatus, appId: respStatus.data.appId } });
                    }, 1000);
                }
            } else {
                showErrorToast(response.message || 'Failed to get user data');
                handleSetIsAuthenticated(false);
            }
        } catch (error) {
            showErrorToast("Error processing OAuth callback: " + error);
            handleSetIsAuthenticated(false);
        }
    };

    useEffect(() => {
        processOAuthResponse();
    }, []);

    return <Loader />;
}

export default AuthCallback;
